/* GRO — collection grid view controls (product / model photos, grid size).

   State lives on the grid wrapper (.gro-view) as data-gro-photo and
   data-gro-density; the CSS reads it directly. This file only answers clicks,
   remembers the choice, and keeps aria-pressed and image sizes in step.

   The saved choice ('gro:grid-view' in localStorage) is applied before first
   paint by the inline script at the top of the section, so nothing here runs
   on the critical path. In the theme editor the choice is not saved — the
   template defaults are what's being judged there. */
(() => {
  const KEY = 'gro:grid-view';
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  const read = () => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || {};
    } catch (e) {
      return {};
    }
  };

  const save = (state) => {
    if (designMode) return;
    try {
      localStorage.setItem(KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode or storage full — the choice just won't persist */
    }
  };

  const syncPressed = (root) => {
    root.querySelectorAll('[data-gro-set-photo]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.groSetPhoto === root.dataset.groPhoto));
    });
    root.querySelectorAll('[data-gro-set-density]').forEach((button) => {
      const pressed =
        button.dataset.groSetDensity === root.dataset.groDensity || button.dataset.groAlso === root.dataset.groDensity;
      button.setAttribute('aria-pressed', String(pressed));
    });
  };

  /* Cards ship a four-across `sizes` fallback. Browsers with sizes="auto" pick
     from layout on their own; for the rest, a size change re-states every
     photo's rendered width so the grid never shows a stretched small file. */
  const resizeImages = (root) => {
    requestAnimationFrame(() => {
      root.querySelectorAll('#product-grid .card__media img, #product-grid .gro-wide-card__media img').forEach((img) => {
        const width = Math.ceil(img.getBoundingClientRect().width);
        if (width > 0) img.sizes = `${width}px`;
      });
    });
  };

  const init = (root) => {
    syncPressed(root);
    if (root.dataset.groDensity !== 'standard') resizeImages(root);

    /* facets.js swaps the grid's HTML on every filter or sort change; the new
       cards arrive with the fallback sizes again. */
    const container = root.querySelector('#ProductGridContainer');
    if (container && !container.groWatched) {
      container.groWatched = true;
      new MutationObserver(() => {
        if (root.dataset.groDensity !== 'standard') resizeImages(root);
      }).observe(container, { childList: true });
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-gro-set-photo], [data-gro-set-density]');
    if (!button) return;
    const root = button.closest('.gro-view');
    if (!root) return;

    const state = read();
    let control = null;
    if (button.dataset.groSetPhoto) {
      if (root.dataset.groPhoto !== button.dataset.groSetPhoto) {
        control = 'photo';
        root.dataset.groViewSource = 'switched';
      }
      root.dataset.groPhoto = button.dataset.groSetPhoto;
      state.photo = button.dataset.groSetPhoto;
    }
    if (button.dataset.groSetDensity) {
      if (root.dataset.groDensity !== button.dataset.groSetDensity) control = 'grid_size';
      root.dataset.groDensity = button.dataset.groSetDensity;
      state.density = button.dataset.groSetDensity;
      resizeImages(root);
    }
    save(state);
    syncPressed(root);

    /* gro-analytics.js reports view changes from this, not from the click, so it always reads
       the view after it has changed. */
    if (control) root.dispatchEvent(new CustomEvent('gro:view-change', { detail: { control } }));
  });

  const initAll = () => document.querySelectorAll('.gro-view').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', initAll);
})();
